import { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import Sidebar from '../components/sidebar/Sidebar';
import TopNav from '../components/topnav/TopNav';
import { Box, useTheme } from '@mui/material';
import './main-layout.scss';
import { useWhatsNew } from '../features/whats-new/hooks/useWhatsNew';
import { CHANGELOG } from '../features/whats-new/configs/changelog';
import WhatsNewModal, { WHATS_NEW_DISMISS_KEY } from '../features/whats-new/components/WhatsNewModal';
import ErrorFallback, { logBoundaryError } from '../components/errors/error-fallback';
import Breadcrumbs from '../components/shared/breadcrumbs/breadcrumbs';

/**
 * Content-area fallback. Lives inside the layout + router, so the Sidebar and
 * TopNav stay interactive and we can recover by navigating rather than reloading.
 */
const RouteErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const navigate = useNavigate();

  return (
    <ErrorFallback
      variant='section'
      error={error}
      onRetry={resetErrorBoundary}
      onGoHome={() => {
        resetErrorBoundary();
        navigate('/');
      }}
    />
  );
};

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const { hasNewUpdates, markAsSeen } = useWhatsNew();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const latestEntry = CHANGELOG[0];

  // Publish the full sticky header height (topnav + breadcrumb) as a CSS var so
  // in-page sticky bars (e.g. tabbed pages) pin directly below it.
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publishHeight = () =>
      document.documentElement.style.setProperty(
        '--topnav-height',
        `${el.offsetHeight}px`
      );
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    window.addEventListener('resize', publishHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publishHeight);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add('sidebar-open');

    return () => {
      document.body.classList.add('sidebar-open');
    };
  }, []);

  // Auto-popup once when there are new updates (unless permanently dismissed)
  useEffect(() => {
    const dismissed = localStorage.getItem(WHATS_NEW_DISMISS_KEY) === 'true';
    if (hasNewUpdates && !dismissed) {
      setWhatsNewOpen(true);
    }
  }, [hasNewUpdates]);

  const handleWhatsNewClose = () => {
    setWhatsNewOpen(false);
    markAsSeen();
  };

  const toggleSidebar = () => {
    document.body.classList.toggle('sidebar-open');
  };

  return (
    <>
      <Sidebar toggleSidebar={toggleSidebar} />
      <Box
        className='main'
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <Box
          ref={headerRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <TopNav />
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1,
              backgroundColor: theme.palette.background.default,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Breadcrumbs />
          </Box>
        </Box>
        <Box className='main__content'>
          <ErrorBoundary
            FallbackComponent={RouteErrorFallback}
            onError={logBoundaryError}
            resetKeys={[location.pathname]}
          >
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
      {latestEntry && (
        <WhatsNewModal
          open={whatsNewOpen}
          onClose={handleWhatsNewClose}
          entry={latestEntry}
        />
      )}
    </>
  );
};

export default MainLayout;
